'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import type { NormalisedHour } from '@/lib/conditions/daily-obs-types';
import type { WeatherSceneProps } from './WeatherScene';
import type { SurfVisualiserForecast } from '@/lib/conditions/visualiser/surf-diorama';
import type { WaterSportsVisualiserForecast } from '@/lib/conditions/visualiser/water-sports-forecast';
import { isWaterSceneSport } from '@/lib/conditions/visualiser/water-sports';
import type { SupProfileKey } from '@/lib/conditions/sport-profiles';

const SurfVisualiser = dynamic(
  () => import('@/app/tools/conditions-visualiser/SurfVisualiser').then((module) => module.SurfVisualiser),
  { ssr: false, loading: () => <div role="status" className="flex aspect-video items-center justify-center rounded-2xl bg-navy/10 text-sm text-navy">Opening the scene…</div> },
);

const WaterSportsVisualiser = dynamic(
  () => import('@/app/tools/conditions-visualiser/WaterSportsVisualiser').then((module) => module.WaterSportsVisualiser),
  { ssr: false, loading: () => <div role="status" className="flex aspect-video items-center justify-center rounded-2xl bg-navy/10 text-sm text-navy">Opening the scene…</div> },
);

// next/dynamic only fetches the chunk when the component first renders, so
// declaring it at module scope keeps the scene out of the initial page bundle
// until the user expands the panel.
const WeatherScene = dynamic(
  () => import('./WeatherScene').then((m) => m.WeatherScene),
  {
    ssr: false,
    loading: () => (
      <div
        className="w-full animate-pulse bg-navy/10 aspect-[3/2] md:aspect-video"
        aria-hidden="true"
      />
    ),
  }
);

type SceneSport = WeatherSceneProps['sport'];

// The location pages use a wider sport vocabulary than the scene. Map the
// extras onto their closest scene treatment.
const SPORT_TO_SCENE: Record<string, SceneSport> = {
  piste: 'piste',
  'off-piste': 'off-piste',
  touring: 'touring',
  mtb: 'mtb',
  surf: 'surf',
  bodyboarding: 'surf',
  wind: 'wind',
  sailing: 'wind',
  foil: 'foil',
  'downwind-foil': 'foil',
  'wing-foil': 'foil',
  sup: 'sup',
  'sup-touring': 'sup-touring',
  'sup-surf': 'sup-surf',
  'sup-downwind': 'sup-downwind',
  diving: 'diving',
  swim: 'swimming',
  swimming: 'swimming',
};

export type ExpandableSceneProps = {
  todayHours: NormalisedHour[] | null;
  tomorrowHours: NormalisedHour[] | null;
  currentHour: number;
  locationType: 'ski' | 'surf';
  sport: string;
  locationMeta: WeatherSceneProps['locationMeta'];
  sportScore?: WeatherSceneProps['sportScore'];
  hourlyScores?: WeatherSceneProps['hourlyScores'];
  waterQuality?: WeatherSceneProps['waterQuality'];
  tidePoints?: WeatherSceneProps['tidePoints'];
  dateKeys?: WeatherSceneProps['dateKeys'];
  surfForecast?: SurfVisualiserForecast;
  waterForecast?: WaterSportsVisualiserForecast;
  allowedSupProfiles?: readonly SupProfileKey[];
};

function MountainGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="24" height="24" rx="7" fill="#1B2840" />
      <path
        d="M4 20.5c1.5-4.8 3.5-8.9 5.5-8.9 1.6 0 2.7 2.4 3.5 4.4.9-3 2.1-6.7 3.7-6.7 1.9 0 3.5 5.4 4.3 11.2H4Z"
        fill="#7A8B6F"
      />
      <path
        d="M14.9 13.4c.5-1.7 1.2-4.1 1.8-4.1.8 0 1.6 1.7 2.2 3.6-.6.6-1.3 1-2 1-.7 0-1.4-.2-2-.5Z"
        fill="#F5F0E8"
      />
    </svg>
  );
}

function WaveGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="24" height="24" rx="7" fill="#1B2840" />
      <path
        d="M5 18.5c0-5.3 2.9-9 6.9-9 3.3 0 5.6 2.2 5.6 4.8 0 2.1-1.5 3.5-3.3 3.5-1.4 0-2.4-.9-2.4-2.1 0-.9.6-1.6 1.5-1.7-.4-.5-1-.8-1.7-.8-1.7 0-2.9 1.6-2.9 3.8v1.5H5Z"
        fill="#7FC4D8"
      />
      <path d="M5.5 21h15" stroke="#3E8FAE" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function ExpandableScene({
  todayHours,
  tomorrowHours,
  currentHour,
  locationType,
  sport,
  locationMeta,
  sportScore,
  hourlyScores,
  waterQuality,
  tidePoints,
  dateKeys,
  surfForecast,
  waterForecast,
  allowedSupProfiles = [],
}: ExpandableSceneProps) {
  const [expanded, setExpanded] = useState(false);

  if (!surfForecast && !waterForecast && (!todayHours || todayHours.length === 0)) return null;

  const sceneSport = SPORT_TO_SCENE[sport] ?? (locationType === 'ski' ? 'piste' : 'surf');

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((o) => !o)}
        aria-expanded={expanded}
        className="w-full min-h-[44px] flex items-center gap-3 rounded-2xl border border-border-light bg-warm-bg px-4 py-2.5 text-left transition-colors hover:bg-stone-100"
      >
        <span className="shrink-0 flex items-center" aria-hidden="true">
          {locationType === 'ski' ? <MountainGlyph /> : <WaveGlyph />}
        </span>
        <span className="text-[14px] font-semibold text-navy">{surfForecast || waterForecast ? 'Watch the week with Colin' : 'Visualise this day'}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className={`ml-auto shrink-0 text-navy transition-transform ${expanded ? 'rotate-180' : ''}`}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {expanded && (
        <div className="mt-3 rounded-2xl overflow-hidden border border-border-light bg-navy">
          {waterForecast && isWaterSceneSport(sport)
            ? <WaterSportsVisualiser key={`${waterForecast.location.slug}:${sport}`} initialForecast={waterForecast} initialSport={sport} allowedSupProfiles={allowedSupProfiles} embedded />
            : surfForecast ? <SurfVisualiser initialForecast={surfForecast} embedded /> : <WeatherScene
            todayHours={todayHours ?? []}
            tomorrowHours={tomorrowHours ?? []}
            currentHour={currentHour}
            locationType={locationType}
            sport={sceneSport}
            locationMeta={locationMeta}
            sportScore={sportScore}
            hourlyScores={hourlyScores}
            waterQuality={waterQuality}
            tidePoints={tidePoints}
            dateKeys={dateKeys}
          />}
        </div>
      )}
    </div>
  );
}
